import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  ArrowLeft, Plus, Trash2, GripVertical, Users, Sliders,
  CheckCircle, AlertCircle, ChevronDown, ChevronUp, X, Play, Save, Search
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useToast } from '../../hooks/useToast'
import filtrosBateriasService from '../../services/filtrosBateriasService'
import leiloesService from '../../services/leiloesService'

const ORDENACAO_OPTIONS = [
  { value: 'CADASTRO', label: 'Ordem de cadastro' },
  { value: 'PESO_ASC', label: 'Menor peso primeiro' },
  { value: 'PESO_DESC', label: 'Maior peso primeiro' },
  { value: 'CURRAL_ASC', label: 'Curral (crescente)' },
  { value: 'CURRAL_DESC', label: 'Curral (decrescente)' },
  { value: 'QTD_DESC', label: 'Mais cabeças primeiro' },
  { value: 'QTD_ASC', label: 'Menos cabeças primeiro' },
  { value: 'ALEATORIO', label: 'Aleatória' },
]

const SEXO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Macho', label: 'Macho' },
  { value: 'Fêmea', label: 'Fêmea' },
]

// Paleta de cores por índice de bateria
const BATERIA_PALETTE = [
  { border: 'border-l-blue-500', badge: 'bg-blue-600', bg: 'bg-blue-50', text: 'text-blue-700' },
  { border: 'border-l-purple-500', badge: 'bg-purple-600', bg: 'bg-purple-50', text: 'text-purple-700' },
  { border: 'border-l-emerald-500', badge: 'bg-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { border: 'border-l-orange-500', badge: 'bg-orange-600', bg: 'bg-orange-50', text: 'text-orange-700' },
  { border: 'border-l-pink-500', badge: 'bg-pink-600', bg: 'bg-pink-50', text: 'text-pink-700' },
  { border: 'border-l-indigo-500', badge: 'bg-indigo-600', bg: 'bg-indigo-50', text: 'text-indigo-700' },
]

const regrasEmpty = () => ({
  _key: Math.random(),
  prioridade: 1,
  qtd_lotes: '',
  tipo_animal: '',
  sexo: '',
  min_animais: '',
  max_animais: '',
  peso_min: '',
  peso_max: '',
  ordenacao_interna: 'CADASTRO',
  racas: [],
  racas_excluir: [],
  currais: [],
})

function RacaCombobox({ racasDisponiveis, racasSelecionadas, onAdd, palette, placeholder = 'Buscar raça...' }) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(t)
  }, [query])

  // Fecha ao clicar fora
  useEffect(() => {
    function onClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const opcoes = racasDisponiveis
    .filter(r => !racasSelecionadas.includes(r.nome))
    .filter(r => !debouncedQuery || r.nome.toLowerCase().includes(debouncedQuery.toLowerCase()))

  function selecionar(nome) {
    onAdd(nome)
    setQuery('')
    setDebouncedQuery('')
    setOpen(false)
    setHighlighted(0)
    inputRef.current?.focus()
  }

  function onKeyDown(e) {
    if (!open) { if (e.key !== 'Escape') setOpen(true); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, opcoes.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (opcoes[highlighted]) selecionar(opcoes[highlighted].nome) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 border border-gray-300 rounded-lg bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
        <Search size={14} className="text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setHighlighted(0) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setDebouncedQuery(''); inputRef.current?.focus() }}>
            <X size={13} className="text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {open && opcoes.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {opcoes.map((r, i) => (
            <button
              key={r.id}
              type="button"
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={e => { e.preventDefault(); selecionar(r.nome) }}
              className={`w-full text-left px-3 py-2 text-sm transition ${
                i === highlighted ? `${palette.bg} ${palette.text} font-medium` : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {r.nome}
            </button>
          ))}
        </div>
      )}

      {open && debouncedQuery && opcoes.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-3 text-sm text-gray-400 text-center">
          Nenhuma raça encontrada
        </div>
      )}
    </div>
  )
}

function FieldInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        {...props}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
      />
    </div>
  )
}

function FieldSelect({ label, children, ...props }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select
        {...props}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
      >
        {children}
      </select>
    </div>
  )
}

export default function FiltrosBateriasForm() {
  const { filtroId } = useParams()
  const isEdit = !!filtroId
  const navigate = useNavigate()
  const { organizadorAtivo } = useOrganizador()
  const toast = useToast()
  const qc = useQueryClient()

  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState('SEXO')
  const [ativo, setAtivo] = useState(true)

  const [sexoPrimeiro, setSexoPrimeiro] = useState('FEMEA')
  const [ordenacaoSexo, setOrdenacaoSexo] = useState('CADASTRO')

  const [regras, setRegras] = useState([{ ...regrasEmpty(), prioridade: 1 }])

  const [validacaoLeilao, setValidacaoLeilao] = useState('')
  const [validacaoResult, setValidacaoResult] = useState(null)
  const [validating, setValidating] = useState(false)
  const [expandedBaterias, setExpandedBaterias] = useState({})

  const { data: leiloes = [] } = useQuery(
    ['leiloes', organizadorAtivo?.id],
    () => leiloesService.getByOrganizador(organizadorAtivo.id),
    { enabled: !!organizadorAtivo?.id }
  )

  const [tipos, setTipos] = useState([])
  useEffect(() => {
    if (!organizadorAtivo?.id) return
    import('../../api/axios').then(({ default: api }) => {
      api.get(`/organizadores/${organizadorAtivo.id}/tipos-animais`)
        .then(r => setTipos(r.data || []))
        .catch(() => {})
    })
  }, [organizadorAtivo?.id])

  const [racasDisponiveis, setRacasDisponiveis] = useState([])
  useEffect(() => {
    if (!organizadorAtivo?.id) return
    import('../../api/axios').then(({ default: api }) => {
      api.get(`/organizadores/${organizadorAtivo.id}/racas`)
        .then(r => setRacasDisponiveis(r.data || []))
        .catch(() => {})
    })
  }, [organizadorAtivo?.id])

  // Carrega o filtro UMA vez por abertura: refetch (foco da janela, cache) nunca
  // pode sobrescrever o que o usuário está editando.
  const hidratadoRef = useRef(null)
  useQuery(
    ['filtro-bateria', organizadorAtivo?.id, filtroId],
    () => filtrosBateriasService.obter(organizadorAtivo.id, filtroId),
    {
      enabled: isEdit && !!organizadorAtivo?.id,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: (data) => {
        if (hidratadoRef.current === filtroId) return
        hidratadoRef.current = filtroId
        setNome(data.nome)
        setTipo(data.tipo)
        setAtivo(data.ativo)
        if (data.tipo === 'SEXO' && data.sexo_config) {
          setSexoPrimeiro(data.sexo_config.sexo_primeiro)
          setOrdenacaoSexo(data.sexo_config.ordenacao_interna)
        }
        if (data.tipo === 'PERSONALIZADO' && data.regras?.length > 0) {
          setRegras(data.regras.map(r => ({ ...r, _key: Math.random(), racas: r.racas || [], racas_excluir: r.racas_excluir || [], currais: r.currais || [] })))
        }
      },
    }
  )

  const { mutate: salvar, isLoading: saving } = useMutation(
    (payload) => isEdit
      ? filtrosBateriasService.atualizar(organizadorAtivo.id, filtroId, payload)
      : filtrosBateriasService.criar(organizadorAtivo.id, payload),
    {
      onSuccess: () => {
        toast.success(isEdit ? 'Filtro atualizado' : 'Filtro criado')
        qc.invalidateQueries(['filtros-baterias', organizadorAtivo?.id])
        qc.removeQueries(['filtro-bateria', organizadorAtivo?.id])
        navigate('/ordem-entrada/filtros-automaticos')
      },
      onError: (err) => {
        const msg = err?.response?.data?.detail || 'Erro ao salvar filtro'
        toast.error(msg)
      },
    }
  )

  const buildPayload = () => ({
    nome,
    tipo,
    ativo,
    sexo_config: tipo === 'SEXO' ? { sexo_primeiro: sexoPrimeiro, ordenacao_interna: ordenacaoSexo } : null,
    regras: tipo === 'PERSONALIZADO'
      ? regras.map((r, i) => ({
          prioridade: i + 1,
          qtd_lotes: r.qtd_lotes ? parseInt(r.qtd_lotes) : null,
          tipo_animal: r.tipo_animal || null,
          sexo: r.sexo || null,
          min_animais: r.min_animais ? parseInt(r.min_animais) : null,
          max_animais: r.max_animais ? parseInt(r.max_animais) : null,
          peso_min: r.peso_min ? parseFloat(r.peso_min) : null,
          peso_max: r.peso_max ? parseFloat(r.peso_max) : null,
          ordenacao_interna: r.ordenacao_interna,
          racas: r.racas || [],
          racas_excluir: r.racas_excluir || [],
          currais: r.currais || [],
        }))
      : [],
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!nome.trim()) return toast.error('Informe um nome para o filtro')
    if (tipo === 'PERSONALIZADO' && regras.length === 0) return toast.error('Adicione ao menos uma regra')
    salvar(buildPayload())
  }

  const addRegra = () =>
    setRegras(prev => [...prev, { ...regrasEmpty(), prioridade: prev.length + 1 }])

  // Currais da bateria: aceita '3', '1-5' ou texto livre ('A2'); separa por vírgula/Enter
  const addCurrais = (key, texto) => {
    const novos = String(texto).split(/[,;\s]+/).map(s => s.trim().toUpperCase()).filter(Boolean)
    if (!novos.length) return
    setRegras(prev => prev.map(r => r._key === key
      ? { ...r, currais: [...(r.currais || []), ...novos.filter(n => !(r.currais || []).includes(n))] }
      : r))
  }
  const removeCurral = (key, valor) =>
    setRegras(prev => prev.map(r => r._key === key ? { ...r, currais: (r.currais || []).filter(c => c !== valor) } : r))

  // Gera uma bateria para cada curral de "de" até "até" (1ª bateria = curral de, 2ª = de+1, ...)
  const [gerarCurral, setGerarCurral] = useState({ aberto: false, de: '1', ate: '' })
  const gerarBateriasPorCurral = () => {
    const de = parseInt(gerarCurral.de)
    const ate = parseInt(gerarCurral.ate)
    if (!Number.isFinite(de) || !Number.isFinite(ate) || ate < de) return toast.error('Informe um intervalo válido (de ≤ até)')
    if (ate - de + 1 > 100) return toast.error('Máximo de 100 baterias por vez')
    const novas = []
    for (let c = de; c <= ate; c++) novas.push({ ...regrasEmpty(), currais: [String(c)] })
    setRegras(prev => {
      const base = prev.length === 1 && !prev[0].qtd_lotes && !prev[0].tipo_animal && !prev[0].sexo && !(prev[0].racas || []).length && !(prev[0].currais || []).length ? [] : prev
      return [...base, ...novas].map((r, i) => ({ ...r, prioridade: i + 1 }))
    })
    setGerarCurral({ aberto: false, de: '1', ate: '' })
    toast.success(`${novas.length} bateria(s) criada(s), uma por curral`)
  }

  // Move a bateria de uma posição para outra (drag & drop ou seletor de posição)
  const moverRegra = (de, para) => {
    if (para === de || para < 0 || para >= regras.length) return
    setRegras(prev => {
      const lista = [...prev]
      const [item] = lista.splice(de, 1)
      lista.splice(para, 0, item)
      return lista.map((r, i) => ({ ...r, prioridade: i + 1 }))
    })
  }

  const removeRegra = (key) =>
    setRegras(prev => prev.filter(r => r._key !== key).map((r, i) => ({ ...r, prioridade: i + 1 })))

  const updateRegra = (key, field, value) =>
    setRegras(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r))

  const addRaca = (key, raca) => {
    setRegras(prev => prev.map(r => {
      if (r._key !== key) return r
      return r.racas.includes(raca) ? r : { ...r, racas: [...r.racas, raca] }
    }))
    setInputRaca(prev => ({ ...prev, [key]: '' }))
  }

  const addRacaExcluir = (key, raca) =>
    setRegras(prev => prev.map(r => r._key === key && !(r.racas_excluir || []).includes(raca)
      ? { ...r, racas_excluir: [...(r.racas_excluir || []), raca] } : r))
  const removeRacaExcluir = (key, raca) =>
    setRegras(prev => prev.map(r => r._key === key ? { ...r, racas_excluir: (r.racas_excluir || []).filter(x => x !== raca) } : r))

  const removeRaca = (key, raca) =>
    setRegras(prev => prev.map(r => r._key === key ? { ...r, racas: r.racas.filter(x => x !== raca) } : r))

  const handleValidar = async () => {
    if (!validacaoLeilao) return toast.error('Selecione um leilão para validar')
    if (!isEdit) return toast.error('Salve o filtro antes de validar')
    setValidating(true)
    setValidacaoResult(null)
    try {
      const result = await filtrosBateriasService.validar(organizadorAtivo.id, filtroId, validacaoLeilao)
      setValidacaoResult(result)
      setExpandedBaterias(Object.fromEntries(result.baterias.map(b => [b.bateria, true])))
    } catch {
      toast.error('Erro ao validar filtro')
    } finally {
      setValidating(false)
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/ordem-entrada/filtros-automaticos')}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 leading-tight">
              {isEdit ? 'Editar Filtro' : 'Novo Filtro'}
            </h1>
            <p className="text-xs text-gray-500">Template de baterias para ordem de entrada</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>{ativo ? 'Ativo' : 'Inativo'}</span>
            <button
              type="button"
              onClick={() => setAtivo(!ativo)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${ativo ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${ativo ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Testar com leilão — só no edit */}
          {isEdit && (
            <>
              <div className="w-px h-6 bg-gray-200" />
              <div className="flex items-center gap-2">
                <select
                  value={validacaoLeilao}
                  onChange={e => { setValidacaoLeilao(e.target.value); setValidacaoResult(null) }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-52"
                >
                  <option value="">Selecionar leilão...</option>
                  {leiloes.map(l => (
                    <option key={l.id} value={l.id}>{l.nome}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleValidar}
                  disabled={validating || !validacaoLeilao}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition whitespace-nowrap"
                >
                  <Play size={13} />
                  {validating ? 'Testando...' : 'Testar'}
                </button>
              </div>
              <div className="w-px h-6 bg-gray-200" />
            </>
          )}

          <button
            onClick={() => navigate('/ordem-entrada/filtros-automaticos')}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition"
          >
            <Save size={15} />
            {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar filtro'}
          </button>
        </div>
      </div>

      {/* Resultados da validação — abaixo do header quando disponível */}
      {isEdit && validacaoResult && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Total', value: validacaoResult.total_lotes, cls: 'bg-gray-50 text-gray-700' },
                  { label: 'Alocados', value: validacaoResult.total_alocados, cls: 'bg-green-50 text-green-700' },
                  { label: 'Sem bateria', value: validacaoResult.total_sem_bateria, cls: validacaoResult.total_sem_bateria > 0 ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-50 text-gray-400' },
                ].map(s => (
                  <div key={s.label} className={`rounded-lg px-4 py-2 text-center ${s.cls}`}>
                    <div className="text-xl font-bold">{s.value}</div>
                    <div className="text-xs mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => setValidacaoResult(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
              <X size={15} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {validacaoResult.baterias.map((bateria, bidx) => {
              const palette = BATERIA_PALETTE[bidx % BATERIA_PALETTE.length]
              return (
                <div key={bateria.bateria} className={`rounded-xl border border-gray-200 border-l-4 ${palette.border} overflow-hidden`}>
                  <button
                    type="button"
                    onClick={() => setExpandedBaterias(prev => ({ ...prev, [bateria.bateria]: !prev[bateria.bateria] }))}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center ${palette.badge}`}>{bidx + 1}</span>
                      <span className="font-semibold text-gray-700 text-xs truncate">{bateria.label}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${palette.bg} ${palette.text} font-medium shrink-0`}>{bateria.lotes.length}</span>
                    </div>
                    {expandedBaterias[bateria.bateria] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                  {expandedBaterias[bateria.bateria] && (
                    <div className="divide-y divide-gray-100 max-h-40 overflow-y-auto">
                      {bateria.lotes.map(lote => (
                        <div key={lote.lote_id} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                          <span className="font-mono text-gray-400 w-7">{lote.numero}</span>
                          <span className="flex-1 text-gray-700 truncate">{lote.descricao || `${lote.tipo_animal} ${lote.sexo}`}</span>
                          <span className="text-gray-400 whitespace-nowrap">{lote.qtd} cab.</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {validacaoResult.lotes_sem_bateria.length > 0 && (
              <div className="border border-yellow-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedBaterias(prev => ({ ...prev, '__sem__': !prev['__sem__'] }))}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-yellow-50 hover:bg-yellow-100 transition"
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle size={13} className="text-yellow-600 shrink-0" />
                    <span className="text-xs font-semibold text-yellow-700">Sem bateria</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">{validacaoResult.lotes_sem_bateria.length}</span>
                  </div>
                  {expandedBaterias['__sem__'] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {expandedBaterias['__sem__'] && (
                  <div className="divide-y divide-yellow-50 max-h-40 overflow-y-auto">
                    {validacaoResult.lotes_sem_bateria.map(lote => (
                      <div key={lote.lote_id} className="px-3 py-1.5 flex items-center gap-2 text-xs bg-yellow-50/30">
                        <span className="font-mono text-gray-400 w-7">{lote.numero}</span>
                        <span className="flex-1 text-gray-700">{lote.tipo_animal} {lote.sexo}</span>
                        <span className="text-gray-400">{lote.qtd} cab.</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo */}
      <div className="space-y-4">

            {/* Nome */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nome do filtro *</label>
              <input
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder="Ex: Leilão Misto Padrão"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Tipo */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Tipo de filtro</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'SEXO', icon: <Users size={22} />, label: 'Por Sexo', desc: 'Duas baterias: Fêmeas e Machos' },
                  { value: 'PERSONALIZADO', icon: <Sliders size={22} />, label: 'Personalizado', desc: 'Monte suas próprias baterias' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTipo(opt.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition ${
                      tipo === opt.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <span className={`shrink-0 ${tipo === opt.value ? 'text-blue-600' : 'text-gray-400'}`}>{opt.icon}</span>
                    <div>
                      <div className={`font-semibold text-sm ${tipo === opt.value ? 'text-blue-900' : 'text-gray-800'}`}>{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Config Por Sexo */}
            {tipo === 'SEXO' && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <h3 className="font-semibold text-gray-800">Configuração das Baterias por Sexo</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Quem entra primeiro?</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ v: 'FEMEA', l: 'Fêmeas primeiro' }, { v: 'MACHO', l: 'Machos primeiro' }].map(opt => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setSexoPrimeiro(opt.v)}
                        className={`py-3 rounded-xl border-2 font-medium text-sm transition ${
                          sexoPrimeiro === opt.v
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <FieldSelect
                  label="Ordenação interna (dentro de cada bateria)"
                  value={ordenacaoSexo}
                  onChange={e => setOrdenacaoSexo(e.target.value)}
                >
                  {ORDENACAO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </FieldSelect>

                {/* Preview */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Preview das baterias</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: `Bateria 1 — ${sexoPrimeiro === 'FEMEA' ? 'Fêmeas' : 'Machos'}`, color: 'blue' },
                      { label: `Bateria 2 — ${sexoPrimeiro === 'FEMEA' ? 'Machos' : 'Fêmeas'}`, color: 'purple' },
                    ].map((b, i) => (
                      <div key={i} className={`rounded-xl border-2 border-${b.color}-200 bg-${b.color}-50 p-4 text-center`}>
                        <div className={`font-semibold text-${b.color}-700 text-sm`}>{b.label}</div>
                        <div className={`text-xs text-${b.color}-500 mt-1`}>
                          {ORDENACAO_OPTIONS.find(o => o.value === ordenacaoSexo)?.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Config Personalizado */}
            {tipo === 'PERSONALIZADO' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">Baterias</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Cada bateria agrupa lotes que atendem suas regras, na ordem de prioridade</p>
                  </div>
                  <button
                    type="button"
                    onClick={addRegra}
                    className="flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition"
                  >
                    <Plus size={14} /> Adicionar bateria
                  </button>
                  <button
                    type="button"
                    onClick={() => setGerarCurral(g => ({ ...g, aberto: !g.aberto }))}
                    className="flex items-center gap-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg font-medium transition"
                    title="Cria uma bateria para cada curral: 1ª bateria = curral 1, 2ª = curral 2..."
                  >
                    Uma bateria por curral
                  </button>
                </div>

                {gerarCurral.aberto && (
                  <div className="flex flex-wrap items-end gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Do curral</label>
                      <input type="number" min="1" value={gerarCurral.de}
                        onChange={e => setGerarCurral(g => ({ ...g, de: e.target.value }))}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Até o curral</label>
                      <input type="number" min="1" value={gerarCurral.ate} placeholder="Ex: 10"
                        onChange={e => setGerarCurral(g => ({ ...g, ate: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); gerarBateriasPorCurral() } }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <button type="button" onClick={gerarBateriasPorCurral}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
                      Criar baterias
                    </button>
                    <p className="text-xs text-gray-500 flex-1 min-w-[220px]">
                      Cria uma bateria por curral, na ordem: a 1ª recebe os lotes do curral inicial, a 2ª do curral seguinte e assim por diante.
                    </p>
                  </div>
                )}

                <DragDropContext onDragEnd={(res) => { if (res.destination) moverRegra(res.source.index, res.destination.index) }}>
                <Droppable droppableId="baterias">
                {(dropProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-4">
                {regras.map((regra, idx) => {
                  const palette = BATERIA_PALETTE[idx % BATERIA_PALETTE.length]
                  const sexoLabel = SEXO_OPTIONS.find(o => o.value === regra.sexo)?.label || 'Todos'
                  const tipoLabel = regra.tipo_animal || 'Todos'
                  return (
                    <Draggable key={regra._key} draggableId={String(regra._key)} index={idx}>
                    {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-white rounded-xl border border-gray-200 border-l-4 ${palette.border} overflow-hidden ${snapshot.isDragging ? 'shadow-2xl ring-2 ring-blue-400' : ''}`}
                    >
                      {/* Header da bateria */}
                      <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <span {...provided.dragHandleProps} title="Arraste para mudar a posição">
                            <GripVertical size={16} className="text-gray-400 cursor-grab" />
                          </span>
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold ${palette.badge}`}>
                            {idx + 1}
                          </span>
                          <span className="font-semibold text-gray-800 text-sm">Bateria {idx + 1}</span>
                          {/* Resumo inline */}
                          <div className="hidden sm:flex items-center gap-2">
                            {regra.sexo && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${palette.bg} ${palette.text} font-medium`}>{sexoLabel}</span>
                            )}
                            {regra.tipo_animal && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{tipoLabel}</span>
                            )}
                            {regra.qtd_lotes && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                máx. {regra.qtd_lotes} lote(s)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                        {regras.length > 1 && (
                          <select
                            value={idx}
                            onChange={e => moverRegra(idx, parseInt(e.target.value))}
                            className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white text-gray-600"
                            title="Mover para a posição"
                          >
                            {regras.map((_, p) => (
                              <option key={p} value={p}>Posição {p + 1}</option>
                            ))}
                          </select>
                        )}
                        {regras.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRegra(regra._key)}
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition"
                            title="Remover bateria"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        </div>
                      </div>

                      {/* Campos */}
                      <div className="p-5">
                        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          <FieldInput
                            label="Qtd. máx. de lotes"
                            type="number"
                            min="1"
                            value={regra.qtd_lotes}
                            onChange={e => updateRegra(regra._key, 'qtd_lotes', e.target.value)}
                            placeholder="Ilimitado"
                          />
                          <FieldSelect
                            label="Tipo de animal"
                            value={regra.tipo_animal}
                            onChange={e => updateRegra(regra._key, 'tipo_animal', e.target.value)}
                          >
                            <option value="">Todos</option>
                            {tipos.map(t => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                          </FieldSelect>
                          <FieldSelect
                            label="Sexo"
                            value={regra.sexo}
                            onChange={e => updateRegra(regra._key, 'sexo', e.target.value)}
                          >
                            {SEXO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </FieldSelect>
                          <FieldSelect
                            label="Ordenação interna"
                            value={regra.ordenacao_interna}
                            onChange={e => updateRegra(regra._key, 'ordenacao_interna', e.target.value)}
                          >
                            {ORDENACAO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </FieldSelect>
                          <FieldInput
                            label="Mín. animais por lote"
                            type="number"
                            min="0"
                            value={regra.min_animais}
                            onChange={e => updateRegra(regra._key, 'min_animais', e.target.value)}
                            placeholder="—"
                          />
                          <FieldInput
                            label="Máx. animais por lote"
                            type="number"
                            min="0"
                            value={regra.max_animais}
                            onChange={e => updateRegra(regra._key, 'max_animais', e.target.value)}
                            placeholder="—"
                          />
                          <FieldInput
                            label="Peso mín. (kg)"
                            type="number"
                            min="0"
                            value={regra.peso_min}
                            onChange={e => updateRegra(regra._key, 'peso_min', e.target.value)}
                            placeholder="—"
                          />
                          <FieldInput
                            label="Peso máx. (kg)"
                            type="number"
                            min="0"
                            value={regra.peso_max}
                            onChange={e => updateRegra(regra._key, 'peso_max', e.target.value)}
                            placeholder="—"
                          />
                        </div>

                        {/* Raças */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <label className="block text-xs font-medium text-gray-500 mb-2">Raças (vazio = todas)</label>
                          {regra.racas.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {regra.racas.map(r => (
                                <span key={r} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${palette.bg} ${palette.text}`}>
                                  {r}
                                  <button type="button" onClick={() => removeRaca(regra._key, r)} className="hover:opacity-70 ml-0.5">
                                    <X size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <RacaCombobox
                            racasDisponiveis={racasDisponiveis}
                            racasSelecionadas={regra.racas}
                            onAdd={nome => addRaca(regra._key, nome)}
                            palette={palette}
                          />

                          <label className="block text-xs font-medium text-gray-500 mt-4 mb-2">Excluir raças (não entram nesta bateria)</label>
                          {(regra.racas_excluir || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {regra.racas_excluir.map(r => (
                                <span key={r} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-red-50 text-red-700">
                                  {r}
                                  <button type="button" onClick={() => removeRacaExcluir(regra._key, r)} className="hover:opacity-70 ml-0.5">
                                    <X size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <RacaCombobox
                            racasDisponiveis={racasDisponiveis}
                            racasSelecionadas={regra.racas_excluir || []}
                            onAdd={nome => addRacaExcluir(regra._key, nome)}
                            palette={{ bg: 'bg-red-50', text: 'text-red-700' }}
                            placeholder="Buscar raça para excluir..."
                          />
                        </div>

                        {/* Currais */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <label className="block text-xs font-medium text-gray-500 mb-2">
                            Currais (vazio = todos) — digite o número, uma faixa como 1-5, ou texto como A2, e tecle Enter
                          </label>
                          {(regra.currais || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {regra.currais.map(c => (
                                <span key={c} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${palette.bg} ${palette.text}`}>
                                  Curral {c}
                                  <button type="button" onClick={() => removeCurral(regra._key, c)} className="hover:opacity-70 ml-0.5">
                                    <X size={10} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <input
                            type="text"
                            placeholder="Ex: 1, 2-5, A2"
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault()
                                addCurrais(regra._key, e.currentTarget.value)
                                e.currentTarget.value = ''
                              }
                            }}
                            onBlur={e => { addCurrais(regra._key, e.currentTarget.value); e.currentTarget.value = '' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                    )}
                    </Draggable>
                  )
                })}
                {dropProvided.placeholder}
                </div>
                )}
                </Droppable>
                </DragDropContext>

                {/* Botão adicionar no rodapé */}
                <button
                  type="button"
                  onClick={addRegra}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition flex items-center justify-center gap-2 font-medium"
                >
                  <Plus size={15} /> Adicionar bateria
                </button>
              </div>
            )}
          </div>

        </div>
  )
}
